
import EventListeners from '~/mixins/EventListeners';
import { getLotValue, getTenderValue } from '~/mixins/getValue';

export default {
  mixins: [
    EventListeners({
      resize () {
        this.updateSize();
      }
    })
  ],

  props: {
    tender: {
      type: Object,
      required: true
    },

    size: {
      type: String,
      validator: value => ['small', 'medium', 'large'].includes(value),
      default: null
    }
  },

  data () {
    return {
      currentLotIndex: 0,
      implicitSize: this.size
    };
  },

  fetch () {
    this.$store.commit('tenderSearchWords/enqueue', this.tender.id);
  },

  computed: {
    latestVersion () {
      return this.tender.tenderVersions[0];
    },

    lots () {
      const comparer = new Intl.Collator('nl', {
        ignorePunctuation: true,
        sensitivity: 'base',
        numeric: true
      });

      const lots = [...(this.tender.tenderVersions[0].lots ?? [])];
      if (lots.length === 0) {
        console.error(`Tender ${this.tender.id} has no lots.`, lots);
      }

      return lots.sort((a, b) =>
        comparer.compare(a.lotNumber, b.lotNumber)
      );
    },

    searchWords () {
      const queue = this.$store.state.tenderSearchWords.queue;
      const result = {
        pending: queue.includes(this.tender.id),
        matches: [],
        words:
          this.$store.state.overview.filters.find(x => x.name === 'scanner')
            ?.options ?? []
      };
      const matches =
        this.$store.state.tenderSearchWords.results[this.tender.id];
      if (!matches) {
        return result;
      }
      const counts = new Map();
      for (const word of matches.flatMap(x => x.matchedWords)) {
        counts.set(word.word, (counts.get(word.word) ?? 0) + 1);
      }
      result.matches = [...counts.entries()].map(x => ({
        word: x[0],
        count: x[1]
      }));
      return result;
    },

    publication () {
      return (
        this.latestVersion.publications.find(x => x.isApplicationPlatform) ??
        this.latestVersion.publications[0]
      );
    },

    status () {
      if (!this.tender.status) {
        return undefined;
      }

      switch (this.tender.status) {
      case 'Active':
        return {
          label: 'Inschrijven op deze aanbesteding is nog mogelijk.',
          color: 'secondary-500'
        };

      case 'Closed':
        return {
          label: 'De mogelijkheid tot inschrijven is verlopen.',
          color: 'error-500'
        };

      case 'Expected':
        return {
          label:
              'Naar verwachting zal deze aanbesteding opnieuw worden gepubliceerd.',
          color: 'base-blue'
        };

      default:
        return undefined;
      }
    }
  },

  watch: {
    'searchWords.words' () {
      this.$fetch();
    }
  },

  beforeDestroy () {
    this.$store.commit('tenderSearchWords/stop');
  },

  mounted () {
    this.updateSize();
  },

  methods: {
    formatLotNumber (lotName) {
      if (/LOT-\d{4}/.test(lotName)) {
        return Number(lotName.split('-')[1]);
      }

      return lotName;
    },

    async createProject (lotNumber) {
      const { id } = await this.$axios.$post('/project', {
        tenderId: this.tender.id,
        lotNumber
      });

      if (!id) {
        return;
      }

      this.$router.push({
        path: '/collaborate/my-tenders',
        query: { destination: this.tender.status === 'Expected' ? 'Expected' : 'Active' },
        hash: `#project-${id}`
      });
    },

    updateSize () {
      if (this.size !== null) { return; }
      if (window.innerWidth < 800) {
        this.implicitSize = 'small';
        return;
      }
      if (window.innerWidth < 1024) {
        this.implicitSize = 'medium';
        return;
      }
      this.implicitSize = 'large';
    },

    getDate (lot) {
      if (!lot) {
        return {
          date: null,
          label: null
        };
      }

      if (this.tender.status === 'Expected') {
        const today = new Date();
        // get the first ExtensionDate or EndDate after today from sortedDates
        const dates = [...lot.planningItems].sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        ).filter(
          x => x.type === 'ExtensionDate' || x.type === 'EndDate'
        );

        const date = [...dates].reverse().find(x => new Date(x.date) > today);

        if (date) {
          return {
            date: date?.date,
            label: date.type === 'ExtensionDate' ? `Eerst volgende verlenging staat gepland op ${this.$options.filters.formatDate(date?.date)}` : `De einddatum van de overeenkomst staat gepland op ${this.$options.filters.formatDate(date?.date)}`
          };
        } else {
          const endDate = dates.find(x => x.type === 'EndDate');
          return {
            date: endDate?.date,
            label: endDate ? `De einddatum van de overeenkomst staat gepland op ${this.$options.filters.formatDate(endDate?.date)}` : ''
          };
        }
      }
      const deadline = lot.planningItems.find(x => x.type === 'ApplicationDeadlineDate');
      return {
        date: deadline?.date,
        label: deadline ? `Deadline ${this.tender.status === 'Closed' ? 'stond' : 'staat'} gepland op ${this.$options.filters.formatDate(deadline?.date)}` : ''
      };
    },

    getTenderValue,
    getLotValue,

    openInNewTab (url) {
      window.open(url);
    },

    openSignin () {
      const publication = this.latestVersion.publications.find(x => x.isApplicationPlatform) ?? this.latestVersion.publications[0];

      window.open(publication.publicationUrl, '_blank');
    }
  }
};
